import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import tecnicoService, { CreateTecnicoDto } from '../../services/tecnicoService';

const defaultForm: CreateTecnicoDto = {
  codigoTecnico: '',
  descripcion: '',
};

const TecnicoForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoTecnico: codigoParam } = useParams();
  const codigoDecoded = codigoParam ? decodeURIComponent(codigoParam) : '';
  const isEdit = !!(codigoParam && codigoParam !== 'nuevo');
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState<CreateTecnicoDto>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTecnico = useCallback(async () => {
    if (!isEdit || !codigoDecoded) return;
    try {
      setLoading(true);
      const tec = await tecnicoService.getTecnico(codigoDecoded);
      setFormData({
        codigoTecnico: tec.codigoTecnico || codigoDecoded,
        descripcion: tec.descripcion || '',
      });
    } catch {
      enqueueSnackbar('Error al cargar el técnico', { variant: 'error' });
      navigate('/servicio/tecnicos');
    } finally {
      setLoading(false);
    }
  }, [isEdit, codigoDecoded, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (isEdit) loadTecnico();
  }, [isEdit, loadTecnico]);

  const handleCancel = useCallback(() => {
    if (saving) return;
    navigate('/servicio/tecnicos');
  }, [navigate, saving]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    try {
      setSaving(true);
      if (!formData.descripcion?.trim()) {
        enqueueSnackbar('El nombre del técnico es requerido', { variant: 'warning' });
        return;
      }
      if (isEdit) {
        await tecnicoService.updateTecnico(codigoDecoded, {
          descripcion: formData.descripcion.trim(),
        });
      } else {
        if (!formData.codigoTecnico?.trim()) {
          enqueueSnackbar('El código del técnico es requerido', { variant: 'warning' });
          return;
        }
        await tecnicoService.createTecnico({
          codigoTecnico: formData.codigoTecnico.trim(),
          descripcion: formData.descripcion.trim(),
        });
      }
      enqueueSnackbar('Técnico guardado exitosamente', { variant: 'success' });
      navigate('/servicio/tecnicos');
    } catch (error: unknown) {
      console.error('Error al guardar técnico:', error);
      const err = error as {
        response?: { data?: { message?: string; errors?: { general?: string[] } } };
      };
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.general?.[0] ||
        'Error al guardar el técnico';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2') {
        e.preventDefault();
        if (!saving) formRef.current?.requestSubmit();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleCancel, saving]);

  if (loading) {
    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: '#f5f5f5',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Button startIcon={<ArrowBack />} onClick={handleCancel} sx={{ textTransform: 'none' }}>
              Técnicos
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isEdit ? 'Editar Técnico' : 'Nuevo Técnico'}
            </Typography>
          </Stack>

          <form ref={formRef} onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Código de Técnico"
                  value={formData.codigoTecnico || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, codigoTecnico: e.target.value }))}
                  fullWidth
                  required
                  disabled={isEdit}
                  autoFocus={!isEdit}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Nombre de Técnico"
                  value={formData.descripcion || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, descripcion: e.target.value }))}
                  fullWidth
                  required
                  autoFocus={isEdit}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'F2-Aceptar'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                ESC-Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TecnicoForm;
